import { BlobClient } from '@azure/storage-blob';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Flex,
  IconButton,
  Progress,
  Spinner,
  Table,
  Tbody,
  Td,
  Tr,
} from '@chakra-ui/react';
import { SpecialZoomLevel, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin, ToolbarProps } from '@react-pdf-viewer/default-layout';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { documentAction } from 'actions';
import renderError from 'components/FilePreview/renderError';
import transformToolbar from 'components/FilePreview/toolbarConfig';
import configs from 'configs';
import { HTTP_CODE_RESPONSE } from 'helpers/constant';
import {
  addTokenURL,
  getOfficeLivePreviewURL,
  isMarkdownFormat,
  isOfficeSupportedType,
  isPDFFormat,
  isSeperatedValueFormat,
  isTxtFormat,
} from 'helpers/file';
import { HTTPMethod } from 'helpers/method';
import Papa from 'papaparse';
import React, { useEffect, useRef, useState } from 'react';
import { MdFileDownload } from 'react-icons/md';
import Markdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { request } from 'services/configs/axiosConfig';
import { UploadService } from 'services/upload/upload.service';
import { downloadFileBlob, getStorageReadBlobToken } from 'utils/blob';
import 'katex/dist/katex.min.css';

const modalDesign = {
  height: '85vh',
  width: '60vw',
  maxWidth: '700px',
};

export type PreviewFileState = {
  url?: string;
  name?: string;
  type?: string;
};

interface FilePreviewDialogProps {
  file: PreviewFileState;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentView = ({ url, type, name }: { url: string; type: string; name: string }) => {
  const dispatch = useDispatch();

  const [csvContent, setCsvContent] = useState<string[][]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [markdownContent, setMarkdownContent] = useState('');
  const [isFileValid, setIsFileValid] = useState(true);

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const readFile = (url: string) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(url);
  };

  const fetchData = (url: string) => {
    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(url);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(url);
        } else if (res.status === HTTP_CODE_RESPONSE.NOT_FOUND) {
          setIsFileValid(false);
        } else {
          setIsFileValid(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const parseCSVData = (url: string) => {
    return new Promise<string[][]>((resolve, reject) => {
      Papa.parse<string[]>(url, {
        download: true,
        skipEmptyLines: true,
        complete: function (results) {
          resolve(results.data?.slice(0, 500));
        },
        error: function (err) {
          reject(err);
        },
      });
    });
  };

  useEffect(() => {
    if (isSeperatedValueFormat(type)) {
      (async () => {
        try {
          setIsLoading(true);
          const CsvData = await parseCSVData(url);
          setCsvContent(CsvData);
        } catch (error) {
          console.log(error);
        }
        setIsLoading(false);
      })();
    }
    if (isMarkdownFormat(type) || isTxtFormat(type)) {
      request({
        url,
        method: HTTPMethod.GET,
        transformRequest: [
          (data, headers) => {
            delete headers['Authorization'];
            return data;
          },
        ],
      })
        .then((data) => setMarkdownContent(data as string))
        .catch((e) => {
          console.log(e);
        });
    }
  }, [url, type]);

  if (!isFileValid) {
    return <>{renderError({ name: 'MissingPDFException' })}</>;
  }

  if (isPDFFormat(type)) {
    // Check if URL is valid before rendering
    if (!url || url.trim() === '') {
      return <>{renderError({ name: 'MissingPDFException' })}</>;
    }

    const zoomPluginInstance = zoomPlugin();
    const renderToolbar = (Toolbar: (_props: ToolbarProps) => React.ReactElement) => (
      <Toolbar>{renderDefaultToolbar((slots) => transformToolbar({ ...slots, Download }))}</Toolbar>
    );

    const getFilePluginInstance = getFilePlugin({
      fileNameGenerator: (file) => {
        let fileName = file.name.substring(file.name.lastIndexOf('/') + 1);
        fileName = fileName.split('?')[0];
        fileName = fileName.substring(fileName.indexOf('_') + 1);
        fileName = decodeURIComponent(fileName);
        return `${fileName}`;
      },
    });

    const { Download } = getFilePluginInstance;

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
      renderToolbar,
    });

    const { renderDefaultToolbar } = defaultLayoutPluginInstance.toolbarPluginInstance;

    return (
      <Box h='100%'>
        <Viewer
          defaultScale={SpecialZoomLevel.PageWidth}
          fileUrl={url}
          plugins={[zoomPluginInstance, defaultLayoutPluginInstance, getFilePluginInstance]}
          renderLoader={renderLoader}
        />
      </Box>
    );
  }

  if (isSeperatedValueFormat(type)) {
    return (
      <Box overflowY='scroll' w='100%' h='100%' fontSize={14}>
        {isLoading ? (
          <Flex align='center' justify='center' w='100%' h='100%'>
            <Spinner size='lg' />
          </Flex>
        ) : (
          <Table size='xl' variant='simple'>
            <Tbody>
              {csvContent?.map((row, rowIndex) => (
                <Tr key={rowIndex}>
                  {row?.map((cell, cellIndex) => (
                    <Td key={cellIndex} p={1} border='border_table_grid'>
                      {cell}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    );
  }

  if (isMarkdownFormat(type) || isTxtFormat(type)) {
    return (
      <Box
        className='markdown'
        overflowY='scroll'
        w='100%'
        h='100%'
        px={6}
        pb={4}
        color='black'
        bg='white'
        border='1px'
      >
        <Markdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
          {markdownContent}
        </Markdown>
      </Box>
    );
  }

  if (isOfficeSupportedType(type)) {
    return (
      <iframe src={getOfficeLivePreviewURL(url)} width='100%' height='100%' title='File Viewer' />
    );
  }

  return <>{renderError({ name: 'ExtensionNotSupported' }, { url, name, type })}</>;
};

const FilePreviewDialog = ({ file, isOpen, onClose }: FilePreviewDialogProps) => {
  const cancelRef = useRef<HTMLDivElement>(null);
  const { name = '', type = '', url = '' } = file;
  const isShowingDownloadIcon = !isOfficeSupportedType(type) && !isPDFFormat(type);

  const downloadFile = async (e: React.MouseEvent<HTMLButtonElement | SVGElement, MouseEvent>) => {
    e.stopPropagation();
    const container = new BlobClient(addTokenURL(url, getStorageReadBlobToken()));
    const downloadBlockBlobResponse = await container.download();
    const blobData = await downloadBlockBlobResponse.blobBody;
    if (blobData) {
      downloadFileBlob(blobData, name);
    }
  };

  return (
    <>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        returnFocusOnClose={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            {...modalDesign}
            w={{ base: '90%', md: '60%' }}
            h={{ base: '70%', md: '80%' }}
          >
            <AlertDialogHeader
              fontSize={{ base: 'small', md: 'medium' }}
              fontWeight='bold'
              textAlign='center'
            >
              <Box px={10}>{name}</Box>
              {isShowingDownloadIcon && (
                <IconButton
                  pos='absolute'
                  top={2}
                  left={2}
                  fontSize={24}
                  border='none'
                  aria-label='Download file'
                  icon={<MdFileDownload />}
                  onClick={downloadFile}
                  variant='outline'
                />
              )}
              <AlertDialogCloseButton
                zIndex={999}
                m={1}
                fontSize={{ base: 'small', md: 'medium' }}
                _focus={{ outline: 'none' }}
              />
            </AlertDialogHeader>

            <AlertDialogBody h='calc(100% - 200px)'>
              <Box h='100%' transform='auto' id='terms_modal_content'>
                <DocumentView url={url} type={type} name={name} />
              </Box>
            </AlertDialogBody>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

const renderLoader = (progress: number) => {
  return (
    <Flex align='center' justify='center' h='600px'>
      <Box w='240px'>
        <Box textAlign='center'>Loading File...</Box>
        <Progress hasStripe value={progress} />
      </Box>
    </Flex>
  );
};

export default FilePreviewDialog;
